import { registerFragment } from '@neptune/cx/registry';
import { DisposalSet } from '@neptune/cx/utils';

import './styles.scss';

const gc = new DisposalSet();

registerFragment('chroa-on-this-page', (fragmentEl, config) => {
    const isEditMode = document.body.classList.contains('has-edit-mode-menu');

    const navList = fragmentEl.querySelector('.chroa-on-this-page__list') as HTMLUListElement | null;

    if (!navList) {
        return;
    }

    /*
     * Config: which heading levels to include and the data attribute that may
     * hold an alternate (shorter) title for the list link.
     */
    const headingDepth = (config?.headingDepth as string) || 'h2';
    const navLabelAttribute = ((config?.navLabelAttribute as string) || 'data-nav-label').trim();
    const headingSelector = headingDepth === 'both' ? 'h2, h3' : headingDepth;

    /*
     * Resolve the container whose headings feed the table of contents.
     * When dropped inside a chroa-sticky-zone we scan that zone's main column;
     * otherwise fall back to the Liferay main content region, then the page.
     */
    const container =
        (fragmentEl.closest('.chroa-sticky-zone')?.querySelector(
            '.chroa-sticky-zone__main'
        ) as HTMLElement | null) ||
        document.getElementById('main-content') ||
        document.querySelector('main') ||
        document.body;

    const headings = Array.from(container.querySelectorAll(headingSelector)).filter(
        (heading) => !fragmentEl.contains(heading)
    ) as HTMLElement[];

    if (headings.length === 0) {
        // Nothing to link to — hide in view mode, keep visible while editing.
        if (!isEditMode) {
            fragmentEl.style.display = 'none';
        }
        return;
    }

    /*
     * Build the link list.
     */
    headings.forEach((heading, index) => {
        if (!heading.id) {
            const safeText = heading.textContent
                ?.trim()
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
            heading.id = safeText ? safeText : `section-${index}`;
        }

        const listItem = document.createElement('li');
        const link = document.createElement('a');

        // Nested indentation for H3s when both levels are shown.
        listItem.classList.add(`chroa-on-this-page__item--${heading.tagName.toLowerCase()}`);

        link.href = `#${heading.id}`;

        const navLabel = navLabelAttribute ? heading.getAttribute(navLabelAttribute) : null;
        link.textContent = (navLabel || heading.textContent || '').trim();

        gc.register(link).addListener('click', (e) => {
            e.preventDefault();
            heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
            history.pushState(null, '', `#${heading.id}`);
        });

        listItem.appendChild(link);
        navList.appendChild(listItem);
    });

    /*
     * Highlight the link for the section currently in view.
     */
    const navLinks = Array.from(
        navList.querySelectorAll('a')
    ) as HTMLAnchorElement[];

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }

                const id = entry.target.getAttribute('id');

                navLinks.forEach((navLink) => navLink.classList.remove('active'));

                const activeLink = navList.querySelector(
                    `a[href="#${id}"]`
                ) as HTMLAnchorElement | null;

                activeLink?.classList.add('active');
            });
        },
        {
            root: null,
            rootMargin: '-15% 0px -60% 0px',
            threshold: 0.0,
        }
    );

    headings.forEach((heading) => observer.observe(heading));
    gc.register(() => observer.disconnect());
});

if (import.meta.hot) {
    import.meta.hot.accept();
    import.meta.hot.dispose(() => gc.disposeAll());
}
